import { use, useMemo } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import type { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import type ObjectSort from 'shared/models/object-sort';

import type { Tenant } from 'features/tenants/models/tenant';
import TenantsProviderContext from 'features/tenants/providers/tenants-provider/contexts/tenants-provider-context';

export type TenantsSortingResult = {
  sortedTenants: Tenant[];
  sort: ObjectSort<Tenant>;
  handleSortChanged: (property: NestedKeyof<Tenant>, isAscending: boolean) => void;
};

const useTenantsSorting = (): TenantsSortingResult => {
  const { readTenantsData } = use(TenantsProviderContext);

  const sessionStorageSortingKey = FilterSessionStorageKeys.TENANTS_SORTING;
  const [sort, setSort] = useSessionStorage<ObjectSort<Tenant>>(sessionStorageSortingKey, {
    property: 'displayName',
    isAscending: true,
  });

  const handleSortChanged = (property: NestedKeyof<Tenant>, isAscending: boolean) => {
    setSort({
      isAscending,
      property,
    });
  };

  const sortedTenants = useMemo(
    () =>
      [...(readTenantsData?.tenants ?? [])].sort((a, b) => {
        const aValue = a[sort.property as keyof Tenant];
        const bValue = b[sort.property as keyof Tenant];

        if (aValue < bValue) {
          return sort.isAscending ? -1 : 1;
        }
        if (aValue > bValue) {
          return sort.isAscending ? 1 : -1;
        }
        return 0;
      }),
    [readTenantsData, sort],
  );

  return {
    sortedTenants,
    sort,
    handleSortChanged,
  };
};

export default useTenantsSorting;
